"use client";

import dynamic from "next/dynamic";
import {ComponentType} from "react";
import {DialogsEnum} from "@/lib/enums/DialogsEnum";

/**
 * Maps a {@link DialogsEnum} to the React component that renders it.
 *
 * Components are code-split (`next/dynamic`) so a dialog's bundle only loads
 * when it is actually shown. To add a new dialog to the orchestrator:
 *   1. add a config entry in `config/dialogs.config.ts`
 *   2. add a row here
 * No component touches z-index, queueing, or visibility logic itself.
 *
 * Every registered component receives, in addition to its own props:
 *   - `open: true`              (it is only mounted while it should be visible)
 *   - `onOpenChange(next)`      (dismiss → closes the instance)
 *   - `orchestratorZIndex`      (apply to the dialog's overlay/content)
 * plus whatever `props` were passed to `orchestrator.open(id, {props})`.
 */
export type OrchestratedDialogProps = {
    open: boolean;
    onOpenChange: (next: boolean) => void;
    orchestratorZIndex: number;
    [key: string]: unknown;
};

// Dialog components have heterogeneous prop shapes; the host supplies them at
// runtime from `orchestrator.open(id, {props})`, so the registry stays loose.
 
type Entry = ComponentType<any>;

export const DIALOG_COMPONENTS: Partial<Record<DialogsEnum, Entry>> = {
    [DialogsEnum.WALLET]: dynamic(() => import("@/app/components/wallet/WalletDialog")),
    [DialogsEnum.SEARCH]: dynamic(() => import("@/app/components/game/SearchDialog")),
    [DialogsEnum.WELCOME_BONUS]: dynamic(
        () => import("@/app/components/bonuses/WelcomeBonusDialog"),
    ),
    [DialogsEnum.WELCOME_BONUS_PROMO]: dynamic(
        () => import("@/app/components/bonuses/WelcomeBonusPromoDialog"),
    ),
    [DialogsEnum.PWA_INSTALL]: dynamic(
        () => import("@/app/components/notifications/PwaInstallPrompt"),
    ),
    [DialogsEnum.PUSH_PERMISSION]: dynamic(
        () => import("@/app/components/notifications/PushPermissionPrompt"),
    ),
    [DialogsEnum.DELETE_ACCOUNT]: dynamic(
        () => import("@/app/components/account/DeleteAccountDialog"),
    ),
    // Bespoke dialogs bridged via adapters (see ./adapters.tsx).
    [DialogsEnum.AUTH]: dynamic(() =>
        import("./adapters").then((m) => m.OrchestratedAuthDialog),
    ),
    [DialogsEnum.SET_NEW_PASSWORD]: dynamic(() =>
        import("./adapters").then((m) => m.OrchestratedSetNewPassword),
    ),
    [DialogsEnum.CONFIRM]: dynamic(() =>
        import("./adapters").then((m) => m.OrchestratedConfirmDialog),
    ),
    [DialogsEnum.CONTINUE_TOP_UP]: dynamic(() =>
        import("./adapters").then((m) => m.OrchestratedContinueTopUp),
    ),
    [DialogsEnum.UNLOCK_NEXT_BONUS]: dynamic(() =>
        import("./adapters").then((m) => m.OrchestratedUnlockNextBonus),
    ),

    // ── Directly compatible (already take `open` + `onOpenChange`) ────────────
    [DialogsEnum.LOG_OUT]: dynamic(() => import("@/app/components/auth/LogOutDialog")),
    [DialogsEnum.FUNDS_CREDITED]: dynamic(
        () => import("@/app/components/wallet/FundsCreditedDialog"),
    ),
    [DialogsEnum.WAGER_REQUIREMENT]: dynamic(
        () => import("@/app/components/wallet/WagerRequirementDialog"),
    ),
    [DialogsEnum.BONUS_CONDITIONS]: dynamic(
        () => import("@/app/components/bonuses/BonusConditionsDialog"),
    ),
    [DialogsEnum.FREE_SPINS_GAME]: dynamic(
        () => import("@/app/components/bonuses/play-free-spins/FreeSpinsGameDialog"),
    ),
    [DialogsEnum.REGIONAL_RESTRICTIONS]: dynamic(
        () => import("@/app/components/auth/RegionalRestrictionsDialog"),
    ),
    [DialogsEnum.PROMO_CODE_RESULT]: dynamic(
        () => import("@/app/components/promo/PromoCodeResultDialog"),
    ),

    // ── Bespoke prop shapes bridged via adapters (see ./adapters.tsx) ─────────
    [DialogsEnum.TX_CANCELLED]: dynamic(() =>
        import("./adapters").then((m) => m.OrchestratedTxCancelledDialog),
    ),
    [DialogsEnum.CONTACT_VERIFY]: dynamic(() =>
        import("./adapters").then((m) => m.OrchestratedContactVerifyModal),
    ),
};
